/*
 * sharedReducer
 * Description
 * The reducer takes care of our data. Using actions, we can
 * update our application state. To add a new action,
 * add it to the switch statement in the reducer function
 *
 * @version 1.0.0
 * @author Giuseppe
 * @since 01-Feb-2023
 */

import produce from 'immer';
import { actionTypes } from '../actions/shared';
import { resetReduxState } from 'Utility/globalMethods';
import { currency } from 'Utility/strings';

const ACTION_HANDLERS = {
	[actionTypes.DO_RESET_SHARED_STATE]: produce((draft, action) => {
		resetReduxState(draft, initialState);
	}),
	// STORE
	[actionTypes.LIST_STORES_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.LIST_STORES_SUCCESS]: produce((draft, action) => {
		draft.storeData = action.response.storeList;
		draft.storePagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.LIST_STORES_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	[actionTypes.FIND_STORE_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FIND_STORE_SUCCESS]: produce((draft, action) => {
		draft.storeData = action.response.stores;
		draft.storePagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.FIND_STORE_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	// GROUP
	[actionTypes.FETCH_GROUP_LIST_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_GROUP_LIST_SUCCESS]: produce((draft, action) => {
		draft.groupData = action.response.groups;
		draft.groupPagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_GROUP_LIST_FAILURE]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_GROUP_FIND_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_GROUP_FIND_SUCCESS]: produce((draft, action) => {
		draft.groupData = action.response.groups;
		draft.groupPagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_GROUP_FIND_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	// MERCHANT
	[actionTypes.FETCH_MERCHANT_LIST_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_MERCHANT_LIST_SUCCESS]: produce((draft, action) => {
		draft.merchantData = action.response.merchantList;
		draft.merchantPagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_MERCHANT_LIST_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_MERCHANT_FIND_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_MERCHANT_FIND_SUCCESS]: produce((draft, action) => {
		draft.merchantData = action.response.merchantList;
		draft.merchantPagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_MERCHANT_FIND_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	// TRANSACTION
	[actionTypes.FETCH_TRANSACTION_LIST_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_TRANSACTION_LIST_SUCCESS]: produce((draft, action) => {
		draft.transactionsData = action.response.transactionList;
		draft.transactionsPagination = action.response.pagination;
		draft.currencyList = currency.map((el) => {
			const cur = {
				content: el.code,
				value: el.code,
			};
			return cur;
		});
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_TRANSACTION_LIST_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_TRANSACTION_FIND_REQUEST]: produce((draft, action) => {
		draft.isLoading = true;
	}),
	[actionTypes.FETCH_TRANSACTION_FIND_SUCCESS]: produce((draft, action) => {
		draft.transactionsData = action.response.transactionList;
		draft.transactionsPagination = action.response.pagination;
		draft.isLoading = false;
	}),
	[actionTypes.FETCH_TRANSACTION_FIND_FAILURE]: produce((draft, action) => {
		draft.isLoading = false;
	}),
	[actionTypes.DO_RESET_SHARED_STORES]: produce((draft, action) => {
		draft.storeData = initialState.storeData;
		draft.storePagination = initialState.storePagination;
	}),
	[actionTypes.DO_RESET_SHARED_GROUPS]: produce((draft, action) => {
		draft.groupData = initialState.groupData;
		draft.groupPagination = initialState.groupPagination;
	}),
	[actionTypes.DO_RESET_SHARED_MERCHANTS]: produce((draft, action) => {
		draft.merchantData = initialState.merchantData;
		draft.merchantPagination = initialState.merchantPagination;
	}),
	[actionTypes.DO_RESET_SHARED_TRANSACTIONS]: produce((draft, action) => {
		draft.transactionsData = initialState.transactionsData;
		draft.transactionsPagination = initialState.transactionsPagination;
	}),
	// @generator reducer:type:action
};

// The initial state of the shared
const initialState = {
	isLoading: false,
	storeData: [],
	transactionsData: [],
	groupData: [],
	merchantData: [],
	storePagination: null,
	transactionsPagination: null,
	groupPagination: null,
	merchantPagination: null,
};

const shared = (state = initialState, action) => {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
};

export default shared;
